"use client";

// Sanity.io imports
import { PortableText, toPlainText } from "next-sanity";

// Component & Template imports
import NextLink from "@/components/interactive/NextLink";
import PlaceholderImage from "@/components/layout/PlaceholderImage";

// Hooks, Utility & Helper function imports
import { createPortableTextComponents } from "@/utils/portableTextComponents";
import formatImageUrl from "@/utils/formatImageUrl";
import classNames from "@/utils/classNames";
import dateString, { validUpdatedAt } from "@/utils/dateString";
import { PRODUCTS } from "@/utils/isovProducts";
import NextImage from "@/components/interactive/NextImage";
import isovTheme from "@/utils/theme";
import { t } from "@/utils/t";

import {
  IsovCard,
  IsovCategory,
  IsovProduct,
  IsovAvatar,
  IsovButton,
} from "@/components/stencil";

interface CardProps {
  theme?: string;
  variant?: string;
  _type?: string;
  type?: string;
  categories?: any;
  mainCategory?: any;
  hideCategoryEyebrow?: boolean;
  logo?: string;
  stepper?: string;
  icon?: any;
  eyebrow?: any;
  headline?: any;
  headlineSize?: string;
  subtext?: any;
  label?: any;
  text?: any;
  excerpt?: any;
  date?: string;
  onDemand?: boolean;
  showDate?: boolean;
  updatedAt?: string;
  publishedAt?: string;
  preferUpdatedAt?: boolean;
  linkLabel?: any;
  linkVariant?: string;
  linkUrl?: string;
  ctas?: any;
  image?: any;
  avatar?: any;
  authors?: any;
  alt?: any;
  locale?: string;
  location?: any;
  alignment?: string;
  restricted?: boolean;
  decorated?: boolean;
  shaded?: boolean;
  className?: string;
  skeleton?: boolean;
}

export function isCardRestricted(type?: string, restricted?: boolean) {
  return type === "media" || Boolean(restricted);
}

export default function Card({
  theme,
  variant,
  _type,
  type,
  categories,
  mainCategory,
  hideCategoryEyebrow,
  logo,
  stepper,
  icon,
  eyebrow,
  headline,
  headlineSize,
  subtext,
  label,
  text,
  excerpt,
  date,
  onDemand,
  showDate,
  updatedAt,
  publishedAt,
  preferUpdatedAt,
  linkLabel,
  linkVariant,
  linkUrl,
  ctas,
  image,
  avatar,
  authors,
  alt,
  locale = "en",
  location,
  alignment,
  restricted,
  decorated,
  shaded,
  className,
  skeleton,
}: CardProps) {
  const ptComponents = createPortableTextComponents(locale);
  const placeholder = PlaceholderImage(true);
  const componentTheme = isovTheme(theme);
  const formattedDate =
    type === "media"
      ? preferUpdatedAt
        ? dateString(
            validUpdatedAt(updatedAt, publishedAt) || date || publishedAt,
          )
        : dateString(date || publishedAt || updatedAt)
      : null;
  const formattedCategories = hideCategoryEyebrow
    ? undefined
    : categories
        ?.map((category: any) => t(category?.label, locale))
        ?.join(" - ") || mainCategory;
  const featuredProduct = icon && PRODUCTS?.includes(icon) ? icon : null;
  const brandIcon = icon && !PRODUCTS?.includes(icon) ? icon : null;
  const allAuthors = authors?.length ? authors : avatar ? [avatar] : null;
  const combinedAuthors = allAuthors
    ?.map((author: any) => t(author?.fullName, locale))
    ?.join(" • ");

  const card = (
    <IsovCard
      theme={theme}
      variant={type === "media" ? "index" : variant}
      type={type}
      stepper={stepper}
      icon={brandIcon}
      eyebrow={
        featuredProduct ? null : formattedCategories || t(eyebrow, locale)
      }
      className={classNames(
        "group-hover:-translate-y-1 transition-all h-full",
        type === "media" && `block rounded-large`,
        `border-${componentTheme.border}`,
        type === "media" &&
          (theme === "dark" || theme === "bright"
            ? `bg-${componentTheme.background}`
            : `bg-${componentTheme.backgroundHero}/10`),
      )}
      headline={t(headline, locale)}
      headlineSize={headlineSize}
      subtext={t(subtext, locale)}
      label={t(label, locale)}
      date={showDate ? formattedDate?.compact : undefined}
      location={t(location, locale)}
      alignment={alignment}
      restricted={isCardRestricted(type, restricted)}
      decorated={decorated}
      shaded={shaded || type === "media"}
      skeleton={skeleton}
    >
      {type !== "media" && categories && (
        <div
          slot="category"
          className="flex flex-wrap gap-3 mb-6 mx-auto sm:mx-0"
        >
          {categories?.map((category: any) => (
            <IsovCategory
              theme={theme}
              key={`${category?._id}-${t(category?.label, locale)}`}
              label={t(category?.label, locale)}
              skeleton={skeleton}
              active={category?.active}
            ></IsovCategory>
          ))}
        </div>
      )}

      {featuredProduct && (
        <IsovProduct
          theme={theme}
          slot="product"
          size="small"
          name={featuredProduct}
          label={t(eyebrow, locale)}
        ></IsovProduct>
      )}

      {logo && (
        <div
          slot="logo"
          className={classNames(
            "w-auto h-10 overflow-hidden object-contain",
            alignment === "left" &&
              "ml-0 justify-items-start justify-start text-left",
            alignment === "center" &&
              "ml-0 sm:mx-auto justify-center justify-items-center text-center",
            alignment === "right" &&
              "ml-0 sm:ml-auto sm:mr-0 justify-items-end justify-end text-right",
            !(type === "media" || type === "index") && "mb-6",
          )}
        >
          <NextImage
            src={logo}
            alt={logo || "Image"}
            className="h-full w-full object-contain"
            maxWidth={300}
            width={300}
            height={110}
            isOptimized
            priority
          />
        </div>
      )}

      {text && (
        <>
          {typeof text === "string" ? (
            <p
              className={classNames(
                "line-clamp-3",
                formattedDate &&
                  (theme === "dark" ? "text-white" : "text-stone"),
              )}
            >
              {text}
            </p>
          ) : (
            <PortableText
              value={t(text, locale)}
              components={ptComponents}
              onMissingComponent={(message, options) => {
                console.warn(message, {
                  type: options.type,
                  nodeType: options.nodeType,
                });
              }}
            />
          )}
        </>
      )}

      {excerpt && typeof excerpt === "string" && (
        <p
          className={classNames(
            "line-clamp-3",
            formattedDate && (theme === "dark" ? "text-white" : "text-stone"),
          )}
        >
          {excerpt}
        </p>
      )}

      {t(excerpt, locale) && (
        <p
          className={classNames(
            "line-clamp-3",
            formattedDate && (theme === "dark" ? "text-white" : "text-stone"),
          )}
        >
          {typeof t(excerpt, locale) === "string"
            ? t(excerpt, locale)
            : toPlainText(t(excerpt, locale))}
        </p>
      )}

      {!linkUrl &&
        ctas?.map((cta: any) => (
          <NextLink
            key={`cta-${cta?._key}-${t(cta?.label, locale)}`}
            href={cta?.url}
            target={cta?.openInNewTab ? "_blank" : undefined}
            type={`button_${cta?.variant ?? "default"}`}
            slot="cta"
          >
            <IsovButton
              theme={theme === "dark" ? "default" : theme}
              variant={cta?.variant}
              label={t(cta?.label, locale)}
              skeleton={skeleton}
            />
          </NextLink>
        ))}

      {!linkUrl && !ctas && (
        <p slot="cta" className="h-1 -mt-1">
          &nbsp;
        </p>
      )}

      {!image && (
        <NextImage
          slot="media"
          src={placeholder?.src}
          alt={placeholder?.altText || placeholder?.alt || "Image"}
          className={`h-full w-full object-contain rounded-medium overflow-hidden`}
          maxWidth={800}
          width={800}
          height={800}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          isOptimized
          loading="lazy"
        />
      )}

      {image && (
        <NextImage
          slot="media"
          src={
            image?.includes("data:image") ? image : formatImageUrl(image, alt)
          }
          alt={alt || "Image"}
          className={classNames(
            "h-full w-full",
            type === "media" && "rounded-medium",
          )}
          maxWidth={800}
          width={800}
          height={800}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          isOptimized={!image?.includes("data:image")}
          loading="lazy"
        />
      )}

      {combinedAuthors && (
        <div
          className="absolute w-full h-full inset-0 z-10"
          slot="avatar"
          title={combinedAuthors}
        ></div>
      )}

      {(_type !== "event" || (_type === "event" && !onDemand)) && location && (
        <IsovAvatar
          theme={theme}
          slot="avatar"
          size="large"
          className="mb-1"
          name={t(location, locale)}
          date={formattedDate?.full}
          skeleton={skeleton}
        />
      )}

      {(_type !== "event" || (_type === "event" && onDemand)) &&
        allAuthors?.map((author: any) => (
          <IsovAvatar
            key={author?._id}
            theme={theme}
            slot="avatar"
            size="small"
            name={t(author?.fullName, locale)}
            // job={t(author?.title, locale)}
            // date={formattedDate?.full}
            skeleton={skeleton}
          >
            {author?.image && (
              <NextImage
                slot="avatar"
                src={author?.image}
                alt={t(author?.fullName, locale) || "Image"}
                maxWidth={100}
                width="100"
                height="100"
                isOptimized
              />
            )}
          </IsovAvatar>
        ))}

      {linkUrl && linkVariant && (
        <div slot="cta" className="flex justify-center">
          <IsovButton
            theme={theme === "dark" ? "default" : theme}
            variant={linkVariant}
            label={t(linkLabel, locale)}
            skeleton={skeleton}
          />
        </div>
      )}
    </IsovCard>
  );

  return linkUrl ? (
    <NextLink
      href={linkUrl}
      className={classNames("w-full group", className)}
      type="button_card"
    >
      {card}
    </NextLink>
  ) : (
    <div className={classNames("md:flex-1", className)}>{card}</div>
  );
}
